<template>
  <div
    id="performance-seating-time-item"
    class="flex items-center justify-start gap-4 relative"
    :class="{ 'opacity-50': isDeleted }"
  >
    <svg
      v-if="isNew || hasUpdates"
      id="indicator"
      viewBox="0 0 5 5"
      width="5"
      height="5"
      class="w-2 h-2 absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2"
      :class="indicatorColor"
    >
      <circle cx="50%" cy="50%" r="50%" />
    </svg>

    <hr
      v-if="isDeleted"
      id="line-through"
      class="w-64 border-linear-strong border-1 absolute top-1/2 left-0 -translate-y-1/2"
    />

    <TextInput
      id="time-input"
      classes="w-40 items-stretch"
      type="time"
      :modelValue="parsedSeatingTime.time24"
      @update:modelValue="updateSeatingTime({ time: settingStore.makeDateTime({ time: $event }) })"
    />
    <NumberInput
      id="maximum-guests-input"
      classes="w-20 items-stretch"
      :modelValue="parsedSeatingTime.maximum_guests"
      @update:modelValue="updateSeatingTime({ maximum_guests: $event })"
    />
    <ArrowReturnLeftIcon
      id="reset-button"
      v-if="hasUpdates"
      class="w-4 h-4 cursor-pointer"
      @click="resetSeatingTime"
    />
    <component
      id="delete-button"
      :is="isDeleted ? UnTrashIcon : TrashIcon"
      class="w-4 h-4 cursor-pointer"
      @click="emit('delete:seatingTime', seatingTime.id)"
    />
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { computed, toRefs } from 'vue'

import ArrowReturnLeftIcon from '@/assets/arrowReturnLeft.svg?component'
import TrashIcon from '@/assets/trash.svg?component'
import UnTrashIcon from '@/assets/unTrash.svg?component'
import NumberInput from '@/common/components/molecules/NumberInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import { ParsedSeatingTime, parseSeatingTime } from '@/models'
import { useSettingStore } from '@/stores/setting'

import { PerformanceSeatingTimeId, PerformanceSeatingTimeRead } from '@generated/types'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:seatingTime', value: PerformanceSeatingTimeRead): void
  // eslint-disable-next-line no-unused-vars
  (event: 'delete:seatingTime', seatingTimeId: PerformanceSeatingTimeId): void
}>()
const props = defineProps<{
  seatingTime: PerformanceSeatingTimeRead
  initialSeatingTime: PerformanceSeatingTimeRead | undefined
  isDeleted: boolean
}>()
const { seatingTime, initialSeatingTime } = toRefs(props)

const parsedSeatingTime = computed<ParsedSeatingTime>(() => parseSeatingTime(seatingTime.value))

const settingStore = useSettingStore()
const hasUpdates = computed(() => {
  if (!initialSeatingTime.value) return false
  return !_.isEqual(seatingTime.value, initialSeatingTime.value)
})
const isNew = computed(() => !initialSeatingTime.value)
const indicatorColor = computed<string>(() => {
  if (isNew.value) return 'fill-status-seated'
  if (hasUpdates.value) return 'fill-status-noshow'
  return ''
})

function resetSeatingTime() {
  emit('update:seatingTime', initialSeatingTime.value)
}
function updateSeatingTime(seatingTimeUpdates: Omit<Partial<PerformanceSeatingTimeRead>, 'id'>) {
  emit('update:seatingTime', { ...seatingTime.value, ...seatingTimeUpdates })
}
</script>
